import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './MarkdownEditor.css';

interface MarkdownEditorProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

type FormatAction = 'bold' | 'italic' | 'heading' | 'ul' | 'ol' | 'link';
type EditorMode = 'write' | 'preview' | 'hints';

// Curated emoji set useful for event / activity / course descriptions.
const EMOJIS = [
  '🎉', '🎈', '🎊', '🥳', '✨', '⭐', '🌟', '🔥',
  '❤️', '💛', '💚', '💙', '💜', '👍', '👏', '🙌',
  '🤸', '💃', '🕺', '⚽', '🏀', '🎾', '🏐', '🏆',
  '🥇', '🎯', '🎨', '🎭', '🎵', '🎶', '🎸', '📣',
  '📅', '🕐', '📍', '🏡', '🌳', '🌲', '🌞', '🌈',
  '🍎', '🍪', '🥤', '🍉', '🚗', '🎒', '✅', 'ℹ️',
];

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  id,
  value,
  onChange,
  placeholder,
  rows = 8,
}) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mode, setMode] = useState<EditorMode>('write');
  const [showEmoji, setShowEmoji] = useState(false);

  // Insert arbitrary text at the cursor (used by the emoji picker).
  const insertAtCursor = useCallback(
    (snippet: string) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        onChange(value + snippet);
        return;
      }
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newText = value.substring(0, start) + snippet + value.substring(end);
      onChange(newText);
      requestAnimationFrame(() => {
        textarea.focus();
        const pos = start + snippet.length;
        textarea.setSelectionRange(pos, pos);
      });
    },
    [value, onChange]
  );

  const applyFormat = useCallback((action: FormatAction) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.substring(start, end);
    const before = value.substring(0, start);
    const after = value.substring(end);

    let newText: string;
    let cursorOffset: number;

    // Find the start of the current line for line-prefix operations
    const lineStart = before.lastIndexOf('\n') + 1;
    const beforeLine = value.substring(0, lineStart);
    const lineContent = selected || value.substring(lineStart, end);

    switch (action) {
      case 'bold':
        if (selected) {
          newText = `${before}**${selected}**${after}`;
          cursorOffset = start + 2 + selected.length + 2;
        } else {
          newText = `${before}**${t('editor.boldText', 'bold text')}**${after}`;
          cursorOffset = start + 2;
        }
        break;

      case 'italic':
        if (selected) {
          newText = `${before}*${selected}*${after}`;
          cursorOffset = start + 1 + selected.length + 1;
        } else {
          newText = `${before}*${t('editor.italicText', 'italic text')}*${after}`;
          cursorOffset = start + 1;
        }
        break;

      case 'heading':
        if (selected) {
          newText = `${beforeLine}## ${value.substring(lineStart, start)}${selected}${after}`;
          cursorOffset = lineStart + 3 + (start - lineStart) + selected.length;
        } else {
          newText = `${beforeLine}## ${lineContent}${after}`;
          cursorOffset = lineStart + 3 + lineContent.length;
        }
        break;

      case 'ul':
        if (selected) {
          // Convert each line to a list item
          const lines = selected.split('\n').map(l => `- ${l}`).join('\n');
          newText = `${before}${lines}${after}`;
          cursorOffset = start + lines.length;
        } else {
          newText = `${beforeLine}- ${value.substring(lineStart, start)}${after}`;
          cursorOffset = lineStart + 2 + (start - lineStart);
        }
        break;

      case 'ol':
        if (selected) {
          const lines = selected.split('\n').map((l, i) => `${i + 1}. ${l}`).join('\n');
          newText = `${before}${lines}${after}`;
          cursorOffset = start + lines.length;
        } else {
          newText = `${beforeLine}1. ${value.substring(lineStart, start)}${after}`;
          cursorOffset = lineStart + 3 + (start - lineStart);
        }
        break;

      case 'link':
        if (selected) {
          newText = `${before}[${selected}](url)${after}`;
          cursorOffset = start + selected.length + 3; // position cursor on "url"
        } else {
          newText = `${before}[${t('editor.linkText', 'link text')}](url)${after}`;
          cursorOffset = start + 1;
        }
        break;

      default:
        return;
    }

    onChange(newText);

    // Restore focus and cursor position after React re-renders
    requestAnimationFrame(() => {
      textarea.focus();
      if (action === 'bold' && !selected) {
        textarea.setSelectionRange(start + 2, start + 2 + t('editor.boldText', 'bold text').length);
      } else if (action === 'italic' && !selected) {
        textarea.setSelectionRange(start + 1, start + 1 + t('editor.italicText', 'italic text').length);
      } else if (action === 'link') {
        if (selected) {
          const urlStart = start + selected.length + 3;
          textarea.setSelectionRange(urlStart, urlStart + 3);
        } else {
          textarea.setSelectionRange(start + 1, start + 1 + t('editor.linkText', 'link text').length);
        }
      } else {
        textarea.setSelectionRange(cursorOffset, cursorOffset);
      }
    });
  }, [value, onChange, t]);

  // Garden-themed cheat-sheet rows: "you type" (left) → "you get" (right).
  const bulletItems = t('editor.hint.bulletItems', 'Bouncy castle|Face painting|Treasure hunt').split('|');
  const numberedItems = t('editor.hint.numberedItems', 'Meet at the gate|Welcome|Games begin').split('|');
  const hintRows: { syntax: string }[] = [
    { syntax: `# ${t('editor.hint.title', "Children's Day in the Garden")}` },
    { syntax: `## ${t('editor.hint.subtitle', 'Afternoon programme')}` },
    { syntax: `**${t('editor.hint.bold', 'Bring good vibes')}**` },
    { syntax: `*${t('editor.hint.italic', 'comfortable clothes')}*` },
    { syntax: bulletItems.map((s) => `- ${s}`).join('\n') },
    { syntax: numberedItems.map((s, i) => `${i + 1}. ${s}`).join('\n') },
    { syntax: `> ${t('editor.hint.quote', 'Free entry for all children 🎈')}` },
    { syntax: `[${t('editor.hint.linkLabel', 'More info')}](https://example.com)` },
  ];

  return (
    <div className="markdown-editor">
      <div className="markdown-editor__tabs">
        <button
          type="button"
          className={`markdown-editor__tab ${mode === 'write' ? 'markdown-editor__tab--active' : ''}`}
          onClick={() => setMode('write')}
        >
          {t('editor.write', 'Write')}
        </button>
        <button
          type="button"
          className={`markdown-editor__tab ${mode === 'preview' ? 'markdown-editor__tab--active' : ''}`}
          onClick={() => setMode('preview')}
        >
          {t('editor.preview', 'Preview')}
        </button>
        <button
          type="button"
          className={`markdown-editor__tab ${mode === 'hints' ? 'markdown-editor__tab--active' : ''}`}
          onClick={() => setMode('hints')}
        >
          {t('editor.hints', 'Hints')}
        </button>
      </div>

      {mode === 'write' && (
        <>
          <div className="markdown-editor__toolbar">
            <button type="button" onClick={() => applyFormat('bold')} title={t('editor.bold', 'Bold')} aria-label={t('editor.bold', 'Bold')}>
              <strong>B</strong>
            </button>
            <button type="button" onClick={() => applyFormat('italic')} title={t('editor.italic', 'Italic')} aria-label={t('editor.italic', 'Italic')}>
              <em>I</em>
            </button>
            <button type="button" onClick={() => applyFormat('heading')} title={t('editor.heading', 'Heading')} aria-label={t('editor.heading', 'Heading')}>
              H
            </button>
            <div className="separator" />
            <button type="button" onClick={() => applyFormat('ul')} title={t('editor.bulletList', 'Bulleted list')} aria-label={t('editor.bulletList', 'Bulleted list')}>
              &bull;&equiv;
            </button>
            <button type="button" onClick={() => applyFormat('ol')} title={t('editor.numberedList', 'Numbered list')} aria-label={t('editor.numberedList', 'Numbered list')}>
              1.
            </button>
            <div className="separator" />
            <button type="button" onClick={() => applyFormat('link')} title={t('editor.link', 'Insert link')} aria-label={t('editor.link', 'Insert link')}>
              &#128279;
            </button>
            <div className="separator" />
            <button
              type="button"
              onClick={() => setShowEmoji((s) => !s)}
              title={t('editor.emoji', 'Emoji')}
              aria-label={t('editor.emoji', 'Emoji')}
              aria-expanded={showEmoji}
              className={showEmoji ? 'markdown-editor__emoji-toggle--active' : ''}
            >
              😊
            </button>
          </div>

          {showEmoji && (
            <div className="markdown-editor__emoji-panel" role="listbox" aria-label={t('editor.emoji', 'Emoji')}>
              {EMOJIS.map((emoji) => (
                <button
                  type="button"
                  key={emoji}
                  className="markdown-editor__emoji"
                  onClick={() => insertAtCursor(emoji)}
                  title={emoji}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          <textarea
            ref={textareaRef}
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
          />
        </>
      )}

      {mode === 'preview' && (
        <div
          className={`markdown-editor__preview ${!value.trim() ? 'markdown-editor__preview--empty' : ''}`}
          style={{ minHeight: `${rows * 1.5}rem` }}
        >
          {value.trim() ? (
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
            </div>
          ) : (
            t('editor.noContent', 'Nothing to preview')
          )}
        </div>
      )}

      {mode === 'hints' && (
        <div className="markdown-editor__hints">
          <p className="markdown-editor__hints-intro">
            {t('editor.hintsIntro', 'Type the text on the left to get the formatting on the right.')}
          </p>
          <div className="markdown-editor__hint-head">
            <span>{t('editor.syntax', 'You type')}</span>
            <span>{t('editor.result', 'You get')}</span>
          </div>
          {hintRows.map((row, i) => (
            <div key={i} className="markdown-editor__hint-row">
              <pre className="markdown-editor__hint-syntax"><code>{row.syntax}</code></pre>
              <div className="markdown-editor__hint-result markdown-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{row.syntax}</ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarkdownEditor;
